import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/LandingPage.css';

const LandingPage = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch company info from API
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get('/api/company-info');
        // Ensure the response has all required fields
        const data = response.data || {};
        if (data.steps && data.benefits && data.pricing) {
          setCompanyInfo(data);
        } else {
          throw new Error('Invalid API response structure');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching company info:', error);
        // Fallback to static data if API fails
        setCompanyInfo({
          name: "zAlt",
          tagline: "AI for Your Time. Time for Your Business.",
          description: "zAlt leverages AI to provide you with your personalised Shift-Scheduler",
          benefits: [
            {
              title: "Find the optimal schedule",
              description: "Focus on your business instead of wasting time on scheduling your team. Once onboarded, we find the optimal schedule for your shifts."
            },
            {
              title: "Connect to your Team",
              description: "Let your employees see their schedules and submit preferences to satisfy your team - we find the perfect balance between employee and business needs."
            },
            {
              title: "Flexible Scheduling",
              description: "You can schedule for any time period you want."
            },
            {
              title: "Understand your shifts",
              description: "We provide insights on your employees and shifts such as recurring patterns, working hours etc."
            }
          ],
          pricing: {
            trial: {
              name: "Test-Trial",
              price: "Free",
              description: "Perfect to get started with our product - let us analyze your business and take care of your schedule for the next month!"
            },
            business: {
              name: "Business Plan",
              price: "$20/month",
              description: "After we have proven our utility to you, we charge you $20 per month plus $2 per employee."
            }
          },
          steps: [
            {
              step: 1,
              title: "Onboarding",
              description: "Join us on a call with our AI agent or chat with us and describe your business shifts and employee constraints."
            },
            {
              step: 2,
              title: "Implementation",
              description: "zAIt builds an intelligent shift scheduler personalized for your business needs and constraints."
            },
            {
              step: 3,
              title: "Manage all Shifts in our Tool",
              description: "zAIt integrates your scheduling in a user-friendly frontend - easy, intuitive, and customized."
            }
          ]
        });
        setLoading(false);
      }
    };

    fetchCompanyInfo();
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    // Observe all cards
    const cards = document.querySelectorAll('.step-card, .benefit-card, .pricing-card');
    cards.forEach(card => observer.observe(card));

    return () => {
      cards.forEach(card => observer.unobserve(card));
    };
  }, [companyInfo]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/contact', contactForm);
      alert(response.data.message);
      setContactForm({ name: '', email: '', company: '', message: '' });
      setShowContactForm(false);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert(`Thank you ${contactForm.name}! We'll get back to you soon.`);
      setContactForm({ name: '', email: '', company: '', message: '' });
      setShowContactForm(false);
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/demo-request', demoForm);
      alert(response.data.message);
      setDemoForm({ name: '', email: '', company: '', phone: '' });
      setShowDemoForm(false);
    } catch (error) {
      console.error('Error submitting demo request:', error);
      alert(`Thank you ${demoForm.name}! We'll contact you to schedule your demo.`);
      setDemoForm({ name: '', email: '', company: '', phone: '' });
      setShowDemoForm(false);
    }
  };

  if (loading || !companyInfo || !companyInfo.steps || !companyInfo.benefits) {
    return (
      <div className="landing-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <h2>{companyInfo.name}</h2>
          </div>
          <div className="nav-links">
            <a href="#home">Home</a>
            <a href="#about">About</a>
            <a href="#contact" onClick={() => setShowContactForm(true)}>Contact</a>
          </div>
          <div className="nav-cta">
            <button className="login-button" onClick={() => navigate('/login')}>
              Login
            </button>
            <button className="cta-button" onClick={() => setShowDemoForm(true)}>
              Try for Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-container">
          <h1>{companyInfo.name}</h1>
          <h2>{companyInfo.tagline}</h2>
          <p>{companyInfo.description}</p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="steps">
        <div className="container">
          <div className="product-button">Product</div>
          <h2>Your AI powered shift scheduler is live in three easy steps:</h2>
          <div className="steps-grid">
            {(companyInfo.steps || []).map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">Step {step.step}</div>
                <div className="step-icon">
                  {index === 0 && <span>💬</span>}
                  {index === 1 && <span>⚙️</span>}
                  {index === 2 && <span>📅</span>}
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
          <div className="product-description">
            <p>With zAIt, we leverage the possibilities of hyper-individualization through LLMs to customize scheduling tools for any business. We offer an out-of-the-box solution that adjusts to every case individually to offer you an affordable scheduling solution - zero technical expertise needed, so you and your employees can focus on their core competencies instead of wasting time with shift scheduling.</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="about" className="benefits">
        <div className="container">
          <div className="benefits-button">Benefits</div>
          <h2>The Key Benefits of AI for Your Business Growth</h2>
          <p>Discover how AI automation enhances efficiency, reduces costs, and drives business growth with smarter, faster processes.</p>
          <div className="benefits-grid">
            {(companyInfo.benefits || []).map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">
                  {index === 0 && <span>⚡</span>}
                  {index === 1 && <span>🤝</span>}
                  {index === 2 && <span>🕐</span>}
                  {index === 3 && <span>📊</span>}
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing">
        <div className="container">
          <div className="pricing-button">Pricing</div>
          <h2>The Best AI for Scheduling, at the Right Price</h2>
          <p>Choose a plan that fits your business needs and start automating with AI</p>
          
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-icon">🚀</div>
              <h3>{companyInfo.pricing.trial.name}</h3>
              <div className="price">{companyInfo.pricing.trial.price}</div>
              <p>{companyInfo.pricing.trial.description}</p>
              <button className="pricing-button-card" onClick={() => setShowDemoForm(true)}>
                Choose this plan
              </button>
            </div>
            <div className="pricing-card featured">
              <div className="pricing-icon">⚡</div>
              <h3>{companyInfo.pricing.business.name}</h3>
              <div className="price">{companyInfo.pricing.business.price}</div>
              <p>{companyInfo.pricing.business.description}</p>
              <button className="pricing-button-card" onClick={() => setShowDemoForm(true)}>
                Choose this plan
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>{companyInfo.name}</h3>
              <p>{companyInfo.tagline}</p>
            </div>
            <div className="footer-links">
              <div className="footer-section">
                <h4>Pages</h4>
                <a href="#home">Home</a>
                <a href="#contact" onClick={() => setShowContactForm(true)}>Contact</a>
              </div>
            <div className="footer-section">
              <h4>Connect</h4>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">Twitter</a>
            </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContactForm && (
        <div className="modal-overlay" onClick={() => setShowContactForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Get in Touch</h3>
            <form onSubmit={handleContactSubmit}>
              <input
                type="text"
                placeholder="Name"
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="Company"
                value={contactForm.company}
                onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                required
              />
              <textarea
                placeholder="Message"
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                required
              />
              <button type="submit">Send Message</button>
              <button type="button" onClick={() => setShowContactForm(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {/* Demo Modal */}
      {showDemoForm && (
        <div className="modal-overlay" onClick={() => setShowDemoForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Schedule Your Demo</h3>
            <form onSubmit={handleDemoSubmit}>
              <input
                type="text"
                placeholder="Name"
                value={demoForm.name}
                onChange={(e) => setDemoForm({...demoForm, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={demoForm.email}
                onChange={(e) => setDemoForm({...demoForm, email: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="Company"
                value={demoForm.company}
                onChange={(e) => setDemoForm({...demoForm, company: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Phone (Optional)"
                value={demoForm.phone}
                onChange={(e) => setDemoForm({...demoForm, phone: e.target.value})}
              />
              <button type="submit">Request Demo</button>
              <button type="button" onClick={() => setShowDemoForm(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;

